import { fbt, useLocaleContext } from 'fbtee'
import { useAuth } from '../../hooks/useAuth'
import { useUserEventInterests } from '../../hooks/useUserEventInterests'
import { useSocialEventData } from '../../hooks/useSocialEventData'
import { useVisibleEvents } from '../../hooks/useVisibleEvents'
import { useEventLineups } from '../../hooks/useEventLineups'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { interestPrompt } from '../../lib/guestPrompts'
import { getLineupNames } from '../../lib/eventUtils'
import { useEventArtistMatches } from '../../hooks/useEventArtistMatches'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { placeholders } from '../../lib/skeletonUtils'
import { EventCard } from '../../components/EventCard'
import { EmptyState } from '../../components/EmptyState'
import { ErrorState } from '../../components/ErrorState'
import type { Event } from '../../types'
import type { ClassifiedError } from '../../lib/errors'
import { SocialEventCard } from '../../components/SocialEventCard'

// A festival's afterparties: the party events whose parentEventId is this
// festival, as the same cards the Parties page uses. Owns its own social
// subscriptions rather than threading them through useEventPageData, since
// they're only needed while this tab is open.
export function AftersView({
  afters,
  loading,
  error,
}: {
  afters: Event[]
  loading: boolean
  error: ClassifiedError | null
}) {
  const { locale } = useLocaleContext()
  const navigate = useTransitionNavigate()
  const { user, loading: authLoading } = useAuth()
  const { eventStatuses, loading: interestsLoading, setEventInterest } =
    useUserEventInterests(user?.uid ?? null)
  const { facepileData, mergedProfiles, currentUserId, allGroupEvents, socialLoading } =
    useSocialEventData(eventStatuses)
  const events = useVisibleEvents(afters, eventStatuses, allGroupEvents)
  const { lineups } = useEventLineups(events)
  const { matchedArtists, lineupSizes } = useEventArtistMatches(user?.uid ?? null)
  const { guard } = useGuestGuard()
  const guardedSetEventInterest = guard(setEventInterest, interestPrompt('event_interest'))

  if (error) return <ErrorState error={error} />

  if (loading) {
    return (
      <div className="space-y-3" data-testid="afters-list">
        {placeholders(2).map((_, i) => <EventCard key={i} loading />)}
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <EmptyState>
        {fbt(
          'No afterparties listed yet.',
          "Empty state on a festival's Afters tab when it has no afterparties listed",
        )}
      </EmptyState>
    )
  }

  return (
    <div className="space-y-3" data-testid="afters-list">
      {events.map(event => (
        <SocialEventCard
          key={event.id}
          event={event}
          showType={false}
          lineup={getLineupNames(event, lineups)}
          followedArtists={matchedArtists.get(event.id)}
          lineupSize={lineupSizes.get(event.id)}
          facepileLoading={socialLoading}
          facepile={facepileData.get(event.id)}
          profiles={mergedProfiles}
          currentUserId={currentUserId}
          locale={locale}
          status={eventStatuses.get(event.id) ?? 'none'}
          statusLoading={authLoading || interestsLoading}
          onOpen={() => navigate(`/events/${event.id}`)}
          onSetStatus={next => guardedSetEventInterest(event.id, next)}
        />
      ))}
    </div>
  )
}
